import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Pencil, Trash2, FileText, Upload, Users, User, Building2, Building } from 'lucide-react';
import { clienti as clientiApi, fatture as fattureApi } from '../utils/api';
import useIsMobile from '../hooks/useIsMobile';
import { useListMode, ListModeToggle } from '../components/shared/ListView';
import DataCard from '../components/shared/DataCard';
import RankingTable, { renderBarraCoda } from '../components/shared/RankingTable';
import NomeGiaPresenteModal from '../components/shared/NomeGiaPresenteModal';
import { nomeGiaPresenteDaErrore } from '../components/shared/nomeGiaPresente';
import { classificaClienti } from './clientiClassifica';

const fmtData = (d) => { if (!d) return '—'; const [y, m, g] = d.split('-'); return `${g}/${m}/${y}`; };
const fmtDataOra = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
         d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

// Helper visivi condivisi (design system)
const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const ROW_BG = (i) => (i % 2 === 0 ? 'var(--ds-bg)' : 'var(--chip)');
const ROW_HOVER = 'color-mix(in srgb, var(--brand) 9%, var(--ds-bg))';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// M23 — palette lifecycle: 5 stati su token semantici/zone (bg via color-mix)
const LIFECYCLE_META = {
  prospect:   { label: 'Prospect',   color: 'var(--ds-muted)' },
  trattativa: { label: 'Trattativa', color: 'var(--warn)' },
  attivo:     { label: 'Attivo',     color: 'var(--ok)' },
  in_pausa:   { label: 'In Pausa',   color: 'var(--zone-amber)' },
  perso:      { label: 'Perso',      color: 'var(--ds-danger)' },
};
const STATO_LIFECYCLE_LIST = ['prospect', 'trattativa', 'attivo', 'in_pausa', 'perso'];

// M23 — matrice transizioni manuali (mirror di backend apps/clienti/lifecycle.py
// TRANSIZIONI_VALIDE). Per ogni stato corrente, gli stati target consentiti.
const TRANSIZIONI_VALIDE = {
  prospect:   ['trattativa', 'perso'],
  trattativa: ['attivo', 'perso'],
  attivo:     ['in_pausa'],
  in_pausa:   ['prospect'],
  perso:      ['prospect'],
};

// Etichetta + token colore del pulsante per ogni stato target (bg/border via color-mix).
const TRANSIZIONE_BTN = {
  trattativa: { label: 'In trattativa', color: 'var(--warn)' },
  attivo:     { label: 'Attivo',        color: 'var(--ok)' },
  in_pausa:   { label: 'In pausa',      color: 'var(--zone-amber)' },
  perso:      { label: 'Perso',         color: 'var(--ds-danger)' },
  prospect:   { label: 'Riattiva',      color: 'var(--brand)' },
};
// Stile bottone transizione derivato dal token colore.
const transBtnStyle = (color, extra = {}) => ({
  border: `1px solid ${tint(color, 35)}`, background: tint(color, 12), color,
  cursor: 'pointer', fontWeight: 500, ...extra,
});

// Transizione "primaria" per la card mobile: la progressione in avanti naturale
// dello stato corrente. Regola = prima target diversa da 'perso' (l'uscita
// negativa non è mai la CTA primaria); fallback al primo target se l'unica
// opzione fosse 'perso'. Le altre transizioni restano nel dettaglio.
// prospect→trattativa · trattativa→attivo · attivo→in_pausa · in_pausa/perso→prospect
const transizionePrimaria = (stato) => {
  const targets = TRANSIZIONI_VALIDE[stato] || [];
  return targets.find((t) => t !== 'perso') || targets[0] || null;
};

const TRIGGER_LABEL = {
  creazione_cliente:    'Creazione',
  preventivo_inviato:   'Preventivo inviato',
  preventivo_accettato: 'Preventivo accettato',
  manuale_artigiano:    'Modifica manuale',
  manuale_admin:        'Modifica admin',
  voice_prospect:       'Voice WhatsApp',
  inattivita_90gg:      'Inattività 90 giorni',
  data_migration_m23:   'Migrazione iniziale',
};

const TIPO_META = {
  privato:    { label: 'Privato',     Icon: User },
  azienda:    { label: 'Azienda',     Icon: Building2 },
  condominio: { label: 'Condominio',  Icon: Building },
};
const TIPI_LIST  = ['privato', 'azienda', 'condominio'];

// Icona + label tipo cliente (sostituisce le emoji 👤🏢🏘️)
function TipoLabel({ tipo, size = 14 }) {
  const m = TIPO_META[tipo];
  if (!m) return <span>{tipo || '—'}</span>;
  const I = m.Icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <I size={size} strokeWidth={2} style={{ color: 'var(--ds-muted)', flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

// M23 — campo `stato` rimosso dal modello: signal setta stato_lifecycle='prospect' al post_save
const FORM_EMPTY = {
  nome: '', tipo: 'privato',
  email: '', telefono: '', piva: '', codice_fiscale: '',
  indirizzo: '', citta: '', provincia: '', note: '',
};

// M23 — badge lifecycle (sostituisce il vecchio StatoBadge a 3 stati)
function LifecycleBadge({ stato, size = 'md' }) {
  const m = LIFECYCLE_META[stato] || { label: stato || '—', color: 'var(--ds-muted)' };
  const small = size === 'sm';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: small ? 5 : 6,
      padding: small ? '2px 8px' : '3px 10px', borderRadius: 99,
      fontSize: small ? 11 : 12, fontWeight: 500,
      background: tint(m.color, 14), color: m.color, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.color }} />
      {m.label}
    </span>
  );
}

function Modal({ title, onClose, children, maxWidth = 600 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: '1px solid var(--ds-border)' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{title}</h2>
          <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--ds-muted)', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── M23 — Mini-modal transizione lifecycle (Segna perso / Riattiva) ──────────
// Niente reload pagina: chiamante riceve l'evento aggiornato + nuovo stato e
// applica setLista()/refresh detail in-place.
function LifecycleTransizioneModal({ cliente, statoTarget, onClose, onSuccess }) {
  const [motivazione, setMotivazione] = useState('');
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState('');

  const motivazioneObbligatoria = statoTarget === 'perso';
  const canConferma = !saving && (!motivazioneObbligatoria || motivazione.trim().length > 0);

  const targetLabel = LIFECYCLE_META[statoTarget]?.label || statoTarget;
  let titolo, cta, intro, placeholder;
  if (statoTarget === 'perso') {
    titolo = `Segna "${cliente.nome}" come perso`;
    cta = 'Conferma perdita';
    intro = 'Indica il motivo della perdita per ricordarlo in futuro. Il cliente potrà sempre essere riattivato.';
    placeholder = 'Es. Ha scelto un altro idraulico, prezzo troppo alto, lavoro rimandato a tempo indefinito...';
  } else if (statoTarget === 'prospect') {
    titolo = `Riattiva "${cliente.nome}"`;
    cta = 'Riattiva cliente';
    intro = 'Il cliente tornerà nello stato "Prospect" e ripartirà il ciclo commerciale. La motivazione è opzionale.';
    placeholder = 'Opzionale — es. Ha richiamato per nuovo bagno';
  } else {
    titolo = `Sposta "${cliente.nome}" in ${targetLabel}`;
    cta = `Sposta in ${targetLabel}`;
    intro = `Confermi il passaggio a "${targetLabel}"? La motivazione è opzionale e verrà salvata nella timeline.`;
    placeholder = 'Opzionale — aggiungi una nota (es. sopralluogo fissato, lavori avviati...)';
  }
  const ctaColor = statoTarget === 'perso' ? 'var(--ds-danger)' : 'var(--brand)';

  const conferma = async () => {
    setSaving(true);
    setErrore('');
    try {
      const r = await clientiApi.transizione(cliente.id, {
        stato_a: statoTarget,
        motivazione: motivazione.trim(),
      });
      onSuccess(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore durante la transizione.';
      setErrore(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={titolo} onClose={saving ? () => {} : onClose} maxWidth={500}>
      <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.5 }}>
        {intro}
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 }}>
          Motivazione {motivazioneObbligatoria && <span style={{ color: 'var(--ds-danger)' }}>*</span>}
        </label>
        <textarea
          autoFocus
          value={motivazione}
          onChange={(e) => setMotivazione(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%', minHeight: 90, padding: '10px 12px', borderRadius: 'var(--radius-base)',
            border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none',
            boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical',
            background: 'var(--ds-bg)', color: 'var(--ink)',
          }}
        />
      </div>
      {errore && (
        <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>
          {errore}
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button
          type="button" onClick={onClose} disabled={saving}
          style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: saving ? 'default' : 'pointer', fontSize: 14, fontWeight: 500 }}
        >Annulla</button>
        <button
          type="button" onClick={conferma} disabled={!canConferma}
          style={{
            padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none',
            background: canConferma ? ctaColor : 'var(--ds-border)',
            color: '#fff', cursor: canConferma ? 'pointer' : 'not-allowed',
            fontSize: 14, fontWeight: 500,
          }}
        >{saving ? 'Salvataggio…' : cta}</button>
      </div>
    </Modal>
  );
}

// ── M23 — Form rapido "Nuovo prospect" 3 campi ───────────────────────────────
function NuovoProspectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ nome: '', telefono: '', interesse: '' });
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState('');
  // DBT-157 — nome già usato: modale, non il JSON grezzo.
  const [nomeInfo, setNomeInfo] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };

  const salva = async () => {
    if (!form.nome.trim()) return;
    setSaving(true);
    setErrore('');
    try {
      // L'interesse va in `note` (non c'è campo dedicato sul modello — coerente con spec "nota sul cliente")
      const payload = {
        nome: form.nome.trim(),
        tipo: 'privato',
        telefono: form.telefono.trim(),
        note: form.interesse.trim() ? `Interesse: ${form.interesse.trim()}` : '',
      };
      const r = await clientiApi.create(payload);
      onCreated(r.data);
    } catch (e) {
      const ng = nomeGiaPresenteDaErrore(e);
      if (ng) { setNomeInfo(ng); return; }
      const msg = e.response?.data ? JSON.stringify(e.response.data) : e.message;
      setErrore('Errore: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <NomeGiaPresenteModal info={nomeInfo} onClose={() => setNomeInfo(null)} onChiudiModulo={onClose} />
    <Modal title="Nuovo prospect" onClose={saving ? () => {} : onClose} maxWidth={480}>
      <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.5 }}>
        Salva al volo un contatto. Lo ritrovi nella lista clienti come <LifecycleBadge stato="prospect" size="sm" /> e puoi completare i dati in seguito.
      </div>
      <form onSubmit={(e) => { e.preventDefault(); salva(); }}>
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Nome <span style={{ color: 'var(--ds-danger)' }}>*</span></label>
          <input autoFocus required data-campo-nome="true" style={inp} value={form.nome} onChange={(e) => set('nome', e.target.value)}
                 placeholder="Es. Mario Rossi" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Telefono</label>
          <input style={inp} value={form.telefono} onChange={(e) => set('telefono', e.target.value)}
                 placeholder="Es. 338 1234567" />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={lbl}>Interesse</label>
          <input style={inp} value={form.interesse} onChange={(e) => set('interesse', e.target.value)}
                 placeholder="Es. Rifacimento bagno" />
        </div>
        {errore && (
          <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>
            {errore}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={saving}
                  style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
          <button type="submit" disabled={saving || !form.nome.trim()}
                  style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none',
                           background: form.nome.trim() ? 'var(--brand)' : 'var(--ds-border)',
                           color: '#fff', cursor: form.nome.trim() ? 'pointer' : 'not-allowed',
                           fontSize: 14, fontWeight: 500 }}>
            {saving ? 'Salvataggio…' : 'Salva prospect'}
          </button>
        </div>
      </form>
    </Modal>
    </>
  );
}

// ── M23 — Form cliente (rimosso campo `stato` — ora gestito da signal) ──────
function ClienteForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...FORM_EMPTY, ...initial });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Nome / Ragione sociale *</label>
        <input required data-campo-nome="true" style={inp} value={form.nome} onChange={(e) => set('nome', e.target.value)} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Tipo</label>
        <select style={inp} value={form.tipo} onChange={(e) => set('tipo', e.target.value)}>
          {TIPI_LIST.map((t) => <option key={t} value={t}>{TIPO_META[t].label}</option>)}
        </select>
      </div>
      <div style={row2}>
        <div><label style={lbl}>Email</label><input style={inp} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
        <div><label style={lbl}>Telefono</label><input style={inp} value={form.telefono} onChange={(e) => set('telefono', e.target.value)} /></div>
      </div>
      <div style={row2}>
        <div><label style={lbl}>Partita IVA</label><input style={inp} value={form.piva} onChange={(e) => set('piva', e.target.value)} /></div>
        <div><label style={lbl}>Codice Fiscale</label><input style={inp} value={form.codice_fiscale} onChange={(e) => set('codice_fiscale', e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={lbl}>Indirizzo</label><input style={inp} value={form.indirizzo} onChange={(e) => set('indirizzo', e.target.value)} /></div>
      <div style={row2}>
        <div><label style={lbl}>Città</label><input style={inp} value={form.citta} onChange={(e) => set('citta', e.target.value)} /></div>
        <div><label style={lbl}>Provincia</label><input style={inp} value={form.provincia} maxLength={5} onChange={(e) => set('provincia', e.target.value)} placeholder="Es. MI" /></div>
      </div>
      <div style={{ marginBottom: 24 }}><label style={lbl}>Note</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
        <button type="submit" disabled={saving} style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>{saving ? 'Salvataggio…' : 'Salva'}</button>
      </div>
    </form>
  );
}

// ── M23 — Scheda dettaglio: badge + timeline 5 eventi + bottoni transizione ──
function ClienteDetail({ cliente, onClose, onEdit, onDelete, onStatoChanged }) {
  const lbl = { fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const val = { fontSize: 14, color: 'var(--ink)', marginTop: 2 };

  // Stato locale del cliente (può cambiare via transizione lifecycle)
  const [c, setC] = useState(cliente);
  const [eventi, setEventi] = useState(null);
  const [loadingEventi, setLoadingEventi] = useState(true);
  const [erroreEventi, setErroreEventi] = useState('');
  const [showEventiTutti, setShowEventiTutti] = useState(false);
  const [showTransizione, setShowTransizione] = useState(null); // 'perso' | 'prospect' | null

  useEffect(() => {
    setLoadingEventi(true);
    setErroreEventi('');
    clientiApi.lifecycle(c.id)
      .then((r) => setEventi(r.data.eventi || []))
      .catch((e) => setErroreEventi(e.response?.data?.detail || 'Errore caricamento timeline'))
      .finally(() => setLoadingEventi(false));
  }, [c.id]);

  const onTransizioneOk = (nuovoEvento) => {
    setShowTransizione(null);
    const updated = { ...c, stato_lifecycle: nuovoEvento.stato_a, lifecycle_aggiornato_il: nuovoEvento.creato_il };
    setC(updated);
    setEventi((prev) => prev ? [nuovoEvento, ...prev] : [nuovoEvento]);
    onStatoChanged && onStatoChanged(updated);
  };

  const stato = c.stato_lifecycle || 'prospect';
  const eventiDaMostrare = eventi ? (showEventiTutti ? eventi : eventi.slice(0, 5)) : [];

  return (
    <Modal title={c.nome} onClose={onClose}>
      {/* Badge lifecycle prominente */}
      <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <LifecycleBadge stato={stato} />
        {c.lifecycle_aggiornato_il && (
          <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>
            aggiornato {fmtDataOra(c.lifecycle_aggiornato_il)}
          </span>
        )}
      </div>

      {/* Bottoni azione */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        <button onClick={() => onEdit(c)} style={{ flex: '1 1 140px', padding: '9px 0', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Pencil size={15} strokeWidth={2} /> Modifica</button>
        {(TRANSIZIONI_VALIDE[stato] || []).map((t) => {
          const b = TRANSIZIONE_BTN[t];
          return (
            <button key={t} onClick={() => setShowTransizione(t)}
                    title={`Sposta in ${b.label}`}
                    style={transBtnStyle(b.color, { flex: '1 1 140px', padding: '9px 0', borderRadius: 'var(--radius-base)', fontSize: 13 })}>
              {b.label}
            </button>
          );
        })}
        <button onClick={() => { if (window.confirm('Eliminare questo cliente?')) onDelete(c.id); }}
                style={transBtnStyle('var(--ds-danger)', { flex: '1 1 140px', padding: '9px 0', borderRadius: 'var(--radius-base)', fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 })}><Trash2 size={15} strokeWidth={2} /> Elimina</button>
      </div>

      {/* Anagrafica */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div><div style={lbl}>Tipo</div><div style={val}><TipoLabel tipo={c.tipo} size={16} /></div></div>
        <div><div style={lbl}>Cliente dal</div><div style={val}>{fmtData(c.created_at?.split('T')[0])}</div></div>
        <div><div style={lbl}>Email</div><div style={val}>{c.email || '—'}</div></div>
        <div><div style={lbl}>Telefono</div><div style={val}>{c.telefono || '—'}</div></div>
        <div><div style={lbl}>P. IVA</div><div style={val}>{c.piva || '—'}</div></div>
        <div><div style={lbl}>Codice Fiscale</div><div style={val}>{c.codice_fiscale || '—'}</div></div>
        <div style={{ gridColumn: '1 / -1' }}><div style={lbl}>Indirizzo</div><div style={val}>{[c.indirizzo, c.citta, c.provincia].filter(Boolean).join(', ') || '—'}</div></div>
        {c.note && <div style={{ gridColumn: '1 / -1', padding: 12, background: 'var(--chip)', borderRadius: 'var(--radius-base)' }}><div style={lbl}>Note</div><div style={{ ...val, whiteSpace: 'pre-wrap' }}>{c.note}</div></div>}
      </div>

      {/* Timeline eventi lifecycle (M23) */}
      <div style={{ borderTop: '1px solid var(--ds-border)', paddingTop: 18 }}>
        <div style={lbl}>Timeline lifecycle</div>
        {loadingEventi ? (
          <div style={{ padding: '14px 0', color: 'var(--ds-muted)', fontSize: 13 }}>Caricamento eventi…</div>
        ) : erroreEventi ? (
          <div style={{ padding: '10px 14px', marginTop: 8, background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13 }}>{erroreEventi}</div>
        ) : !eventi || eventi.length === 0 ? (
          <div style={{ padding: '14px 0', color: 'var(--ds-muted)', fontSize: 13 }}>Nessun evento registrato.</div>
        ) : (
          <>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 0 }}>
              {eventiDaMostrare.map((ev, idx) => (
                <div key={ev.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderTop: idx === 0 ? 'none' : '1px solid var(--ds-border)' }}>
                  <div style={{ flexShrink: 0, paddingTop: 2 }}>
                    <LifecycleBadge stato={ev.stato_a} size="sm" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>
                      {ev.stato_da
                        ? `${LIFECYCLE_META[ev.stato_da]?.label || ev.stato_da} → ${LIFECYCLE_META[ev.stato_a]?.label || ev.stato_a}`
                        : `Stato iniziale: ${LIFECYCLE_META[ev.stato_a]?.label || ev.stato_a}`}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 2 }}>
                      {TRIGGER_LABEL[ev.trigger] || ev.trigger} · {fmtDataOra(ev.creato_il)}
                      {ev.creato_da_email && ` · ${ev.creato_da_email}`}
                    </div>
                    {ev.motivazione && (
                      <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 4, fontStyle: 'italic' }}>
                        "{ev.motivazione}"
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {eventi.length > 5 && (
              <button onClick={() => setShowEventiTutti((v) => !v)}
                      style={{ marginTop: 8, padding: '6px 12px', background: 'transparent', border: 'none', color: 'var(--brand)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                {showEventiTutti ? `Mostra solo gli ultimi 5` : `Mostra tutti (${eventi.length})`}
              </button>
            )}
          </>
        )}
      </div>

      {showTransizione && (
        <LifecycleTransizioneModal
          cliente={c}
          statoTarget={showTransizione}
          onClose={() => setShowTransizione(null)}
          onSuccess={onTransizioneOk}
        />
      )}
    </Modal>
  );
}

function ImportClientePdfModal({ onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [confidenza, setConfidenza] = useState('');
  const [form, setForm] = useState({ ...FORM_EMPTY });
  const [saving, setSaving] = useState(false);
  // DBT-157 — nome già usato: modale, non il JSON grezzo.
  const [nomeInfo, setNomeInfo] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const analizza = async () => {
    if (!file) return;
    setLoading(true);
    setErrore('');
    try {
      const r = await clientiApi.importaPdf(file);
      const data = r.data;
      setConfidenza(data.confidenza);
      const e = data.estratto;
      setForm({
        nome: e.nome || '',
        tipo: ['privato', 'azienda', 'condominio'].includes(e.tipo) ? e.tipo : 'privato',
        email: e.email || '',
        telefono: e.telefono || '',
        piva: e.piva || '',
        codice_fiscale: e.codice_fiscale || '',
        indirizzo: e.indirizzo || '',
        citta: e.citta || '',
        provincia: e.provincia || '',
        note: '',
      });
      setStep(2);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore durante l\'analisi del documento.';
      setErrore(msg);
    } finally {
      setLoading(false);
    }
  };

  const salvaCliente = async () => {
    setSaving(true);
    try {
      const r = await clientiApi.create(form);
      onImported(r.data);
      onClose();
    } catch (err) {
      const ng = nomeGiaPresenteDaErrore(err);
      if (ng) { setNomeInfo(ng); return; }
      const msg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
      setErrore('Errore nel salvataggio: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };
  const CONFIDENZA_META = {
    alta:  { label: 'Confidenza alta',  color: 'var(--ok)' },
    media: { label: 'Confidenza media', color: 'var(--warn)' },
    bassa: { label: 'Confidenza bassa', color: 'var(--ds-danger)' },
  };
  const confMeta = CONFIDENZA_META[confidenza] || CONFIDENZA_META.bassa;

  return (
    <>
    <NomeGiaPresenteModal info={nomeInfo} onClose={() => setNomeInfo(null)} onChiudiModulo={onClose} />
    <Modal title="Importa cliente da PDF" onClose={onClose}>
      {step === 1 && (
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--brand)'; }}
            onDragLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; }}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--ds-border)'; const f = e.dataTransfer.files[0]; if (f && f.type === 'application/pdf') setFile(f); }}
            style={{ border: '2px dashed var(--ds-border)', borderRadius: 'var(--radius-card)', padding: '40px 20px', textAlign: 'center', marginBottom: 20, cursor: 'pointer', transition: 'border-color 0.2s' }}
            onClick={() => document.getElementById('import-cliente-pdf-input').click()}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: 'var(--ds-muted)' }}>
              <Upload size={36} strokeWidth={1.5} />
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500, marginBottom: 4 }}>
              {file ? file.name : 'Trascina un PDF o clicca per selezionare'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>
              Contratti, preventivi, fatture, visure camerali
            </div>
            <input id="import-cliente-pdf-input" type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
          </div>
          {errore && <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>{errore}</div>}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
            <button onClick={analizza} disabled={!file || loading}
              style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: !file ? 'var(--ds-border)' : 'var(--brand)', color: '#fff', cursor: file ? 'pointer' : 'default', fontSize: 14, fontWeight: 500 }}>
              {loading ? 'mAIstro sta leggendo il documento...' : 'Analizza documento →'}
            </button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: tint(confMeta.color, 14), color: confMeta.color }}>{confMeta.label}</span>
            <span style={{ fontSize: 13, color: 'var(--ds-muted)' }}>Verifica i dati estratti e correggi se necessario</span>
          </div>
          {errore && <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>{errore}</div>}
          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Nome / Ragione sociale *</label>
            <input required data-campo-nome="true" style={inp} value={form.nome} onChange={(e) => set('nome', e.target.value)} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Tipo</label>
            <select style={inp} value={form.tipo} onChange={(e) => set('tipo', e.target.value)}>
              {TIPI_LIST.map((t) => <option key={t} value={t}>{TIPO_META[t].label}</option>)}
            </select>
          </div>
          <div style={row2}>
            <div><label style={lbl}>Email</label><input style={inp} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
            <div><label style={lbl}>Telefono</label><input style={inp} value={form.telefono} onChange={(e) => set('telefono', e.target.value)} /></div>
          </div>
          <div style={row2}>
            <div><label style={lbl}>Partita IVA</label><input style={inp} value={form.piva} onChange={(e) => set('piva', e.target.value)} /></div>
            <div><label style={lbl}>Codice Fiscale</label><input style={inp} value={form.codice_fiscale} onChange={(e) => set('codice_fiscale', e.target.value)} /></div>
          </div>
          <div style={{ marginBottom: 16 }}><label style={lbl}>Indirizzo</label><input style={inp} value={form.indirizzo} onChange={(e) => set('indirizzo', e.target.value)} /></div>
          <div style={row2}>
            <div><label style={lbl}>Citta</label><input style={inp} value={form.citta} onChange={(e) => set('citta', e.target.value)} /></div>
            <div><label style={lbl}>Provincia</label><input style={inp} value={form.provincia} maxLength={5} onChange={(e) => set('provincia', e.target.value)} placeholder="Es. MI" /></div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={() => { setStep(1); setErrore(''); }} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>← Indietro</button>
            <button onClick={salvaCliente} disabled={saving || !form.nome}
              style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              {saving ? 'Salvataggio...' : 'Salva cliente'}
            </button>
          </div>
        </div>
      )}
    </Modal>
    </>
  );
}

export default function Clienti() {
  const isMobile = useIsMobile();
  const { isCards, mode, setMode } = useListMode('clienti', isMobile);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Deep-link da Controllo gestionale (funnel): /clienti?stato_lifecycle=prospect
  // pre-applica il filtro (come Fatture/Preventivi con ?stato=).
  const [filtroStato, setFiltroStato] = useState(() => {
    const s = new URLSearchParams(window.location.search).get('stato_lifecycle');
    return STATO_LIFECYCLE_LIST.includes(s) ? s : '';
  });
  const [showForm, setShowForm] = useState(false);
  const [showProspect, setShowProspect] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [showImportPdf, setShowImportPdf] = useState(false);
  const [rankingClienti, setRankingClienti] = useState([]);
  const [transizione, setTransizione] = useState(null); // { cliente, statoTarget }
  // DBT-157 — creazione o rinomina verso un nome già usato: modale dedicato.
  const [nomeInfo, setNomeInfo] = useState(null);
  const location = useLocation();

  // Apri form automaticamente se navigazione da Home/sidebar (+Nuovo cliente)
  useEffect(() => {
    if (location.pathname === '/clienti/nuovo') { setEditing(null); setShowForm(true); }
  }, []); // eslint-disable-line

  // Deep-link da Home "Cosa fare oggi": /clienti?apri={id} apre la scheda cliente
  useEffect(() => {
    const apriId = new URLSearchParams(location.search).get('apri');
    if (apriId) clientiApi.get(apriId).then(r => setDettaglio(r.data)).catch(() => {});
  }, [location.search]); // eslint-disable-line

  useEffect(() => {
    fattureApi.list({ page_size: 200 }).then(r => {
      // DBT-053 (13/10) — le annullate non contano (clientiClassifica.js).
      setRankingClienti(classificaClienti(r.data.results || r.data));
    }).catch(() => {});
  }, []);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      // M23 — querystring rinominato da `?stato=` a `?stato_lifecycle=`
      if (filtroStato) params.stato_lifecycle = filtroStato;
      const r = await clientiApi.list(params);
      setLista(r.data.results ?? r.data);
    } catch {} finally { setLoading(false); }
  }, [search, filtroStato]);

  useEffect(() => { carica(); }, [carica]);

  const salva = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        const r = await clientiApi.update(editing.id, form);
        setLista((prev) => prev.map((c) => c.id === editing.id ? r.data : c));
      } else {
        const r = await clientiApi.create(form);
        setLista((prev) => [r.data, ...prev]);
      }
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      const ng = nomeGiaPresenteDaErrore(e);
      if (ng) { setNomeInfo(ng); return; }
      alert('Errore: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    }
    finally { setSaving(false); }
  };

  // DBT-157 — «Apri il cliente esistente»: chiude il modulo e apre la sua scheda.
  const apriClienteEsistente = (id) => {
    setShowForm(false);
    setEditing(null);
    clientiApi.get(id).then((r) => setDettaglio(r.data)).catch(() => {});
  };

  const elimina = async (id) => {
    try { await clientiApi.delete(id); setDettaglio(null); carica(); }
    catch { alert('Errore nell\'eliminazione'); }
  };

  // M23 — transizione lifecycle dalla lista (mini-modal inline, no navigazione)
  const onTransizioneRiga = (cliente, statoTarget) => {
    setTransizione({ cliente, statoTarget });
  };
  const onTransizioneOk = (nuovoEvento) => {
    if (!transizione) return;
    const cid = transizione.cliente.id;
    setLista((prev) => prev.map((c) => c.id === cid
      ? { ...c, stato_lifecycle: nuovoEvento.stato_a, lifecycle_aggiornato_il: nuovoEvento.creato_il }
      : c));
    setTransizione(null);
  };

  // M23 — Stats locali su 5 stati lifecycle (era: 3 stati legacy)
  const stats = {
    totale: lista.length,
    prospect: lista.filter((c) => c.stato_lifecycle === 'prospect').length,
    trattativa: lista.filter((c) => c.stato_lifecycle === 'trattativa').length,
    attivi: lista.filter((c) => c.stato_lifecycle === 'attivo').length,
    in_pausa: lista.filter((c) => c.stato_lifecycle === 'in_pausa').length,
    persi: lista.filter((c) => c.stato_lifecycle === 'perso').length,
  };

  // Corpo lista: loading → empty → (mobile: card | desktop: tabella). Condiviso
  // dai due layout così i rami loading/empty non si duplicano.
  const listaBody = loading ? (
    <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ds-muted)' }}>Caricamento…</div>
  ) : lista.length === 0 ? (
    <div style={{ textAlign: 'center', padding: '60px 0', background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14, color: 'var(--ds-muted)' }}><Users size={44} strokeWidth={1.5} /></div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>Nessun cliente trovato</div>
      <div style={{ fontSize: 14, color: 'var(--ds-muted)', marginBottom: 24 }}>{search || filtroStato ? 'Prova a cambiare i filtri' : 'Aggiungi il tuo primo cliente o prospect'}</div>
      {!search && !filtroStato && <button onClick={() => setShowProspect(true)} style={{ padding: '11px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>+ Nuovo prospect</button>}
    </div>
  ) : isCards ? (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? 10 : 14 }}>
      {lista.map((c) => {
        const stato = c.stato_lifecycle || 'prospect';
        const primaria = transizionePrimaria(stato);
        const b = primaria ? TRANSIZIONE_BTN[primaria] : null;
        return (
          <DataCard
            key={c.id}
            title={c.nome}
            subtitle={c.citta || null}
            onClick={() => setDettaglio(c)}
            badge={<LifecycleBadge stato={stato} size="sm" />}
            meta={[
              { label: 'Tipo', value: <TipoLabel tipo={c.tipo} size={13} /> },
              c.telefono ? { label: 'Telefono', value: c.telefono } : null,
              c.email ? { label: 'Email', value: c.email } : null,
            ]}
            actions={primaria ? (
              <button
                onClick={() => onTransizioneRiga(c, primaria)}
                title={`Sposta in ${b.label}`}
                style={transBtnStyle(b.color, { padding: '8px 14px', borderRadius: 'var(--radius-base)', fontSize: 13 })}
              >{b.label}</button>
            ) : null}
          />
        );
      })}
    </div>
  ) : (
    <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ds-border)' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>Elenco Clienti</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: 'var(--chip)', borderBottom: '2px solid var(--ds-border)' }}>
          {['Cliente', 'Tipo', 'Email', 'Telefono', 'Città', 'Stato', 'Azioni'].map((h) => (
            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {lista.map((c, i) => {
            const stato = c.stato_lifecycle || 'prospect';
            const targets = TRANSIZIONI_VALIDE[stato] || [];
            return (
              <tr key={c.id} style={{ borderTop: '1px solid var(--ds-border)', background: ROW_BG(i) }}
                onMouseEnter={(e) => e.currentTarget.style.background = ROW_HOVER}
                onMouseLeave={(e) => e.currentTarget.style.background = ROW_BG(i)}>
                <td style={{ padding: '13px 16px', cursor: 'pointer' }} onClick={() => setDettaglio(c)}>
                  <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--ink)' }}>{c.nome}</div>
                </td>
                <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', cursor: 'pointer' }} onClick={() => setDettaglio(c)}><TipoLabel tipo={c.tipo} size={13} /></td>
                <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', cursor: 'pointer' }} onClick={() => setDettaglio(c)}>{c.email || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', cursor: 'pointer' }} onClick={() => setDettaglio(c)}>{c.telefono || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', cursor: 'pointer' }} onClick={() => setDettaglio(c)}>{c.citta || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                <td style={{ padding: '13px 16px', cursor: 'pointer' }} onClick={() => setDettaglio(c)}>
                  <LifecycleBadge stato={stato} size="sm" />
                </td>
                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                  {targets.length === 0 ? (
                    <span style={{ color: 'var(--ds-muted)', fontSize: 11 }}>—</span>
                  ) : (
                    <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                      {targets.map((t) => {
                        const b = TRANSIZIONE_BTN[t];
                        return (
                          <button key={t}
                            onClick={(e) => { e.stopPropagation(); onTransizioneRiga(c, t); }}
                            title={`Sposta in ${b.label}`}
                            style={transBtnStyle(b.color, { padding: '6px 10px', borderRadius: 'var(--radius-base)', fontSize: 11 })}
                          >{b.label}</button>
                        );
                      })}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ padding: '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: 'var(--ink)' }}>Clienti</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ds-muted)' }}>Anagrafica e gestione clienti</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => setShowImportPdf(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 22px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>
            <FileText size={15} strokeWidth={2} />
            Importa da PDF
          </button>
          {/* M23 — Nuovo prospect: form rapido 3 campi */}
          <button onClick={() => setShowProspect(true)} style={{ padding: '11px 22px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>
            + Nuovo prospect
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: '11px 22px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, boxShadow: '0 4px 14px rgba(46,160,132,0.35)' }}>+ Nuovo cliente</button>
        </div>
      </div>

      {/* Stats — M23: 5 stati */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Totale',     value: stats.totale,     color: 'var(--brand)' },
          { label: 'Prospect',   value: stats.prospect,   color: 'var(--ds-muted)' },
          { label: 'Trattativa', value: stats.trattativa, color: 'var(--warn)' },
          { label: 'Attivi',     value: stats.attivi,     color: 'var(--ok)' },
          { label: 'In pausa',   value: stats.in_pausa,   color: 'var(--zone-amber)' },
          { label: 'Persi',      value: stats.persi,      color: 'var(--ds-danger)' },
        ].map((s) => (
          <div key={s.label} style={{ background: 'var(--chip)', borderRadius: 'var(--radius-card)', padding: '20px 24px', border: '1px solid var(--ds-border)', borderTop: `3px solid ${s.color}`, boxShadow: CARD_SHADOW }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--ink)', marginTop: 4 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filtri — M23: stato_lifecycle */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ds-muted)', display: 'flex' }}><Search size={16} strokeWidth={2} /></span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome, email…"
            style={{ width: '100%', padding: '10px 12px 10px 36px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' }} />
        </div>
        <button onClick={() => setFiltroStato('')} style={{
          padding: '10px 16px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
          background: !filtroStato ? 'var(--brand)' : 'var(--ds-bg)',
          color: !filtroStato ? '#fff' : 'var(--ds-text)',
          border: !filtroStato ? '1px solid var(--brand)' : '1px solid var(--ds-border)',
        }}>Tutti</button>
        <select value={filtroStato} onChange={(e) => setFiltroStato(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', outline: 'none', fontFamily: 'inherit' }}>
          <option value="">Filtra stato…</option>
          {STATO_LIFECYCLE_LIST.map((s) => <option key={s} value={s}>{LIFECYCLE_META[s].label}</option>)}
        </select>
        {!isMobile && (
          <div style={{ marginLeft: 'auto' }}>
            <ListModeToggle mode={mode} setMode={setMode} />
          </div>
        )}
      </div>

      {/* Lista + Ranking */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {listaBody}
          <RankingTable
            title="Top Clienti per Fatturato"
            colNome="CLIENTE"
            colValore="FATTURATO"
            colCoda="% SUL TOTALE"
            rows={rankingClienti}
            getSub={(row) => `${Number(row.valore).toLocaleString('it-IT')} €`}
            renderCoda={renderBarraCoda}
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '5fr 2fr', gap: 24, alignItems: 'start' }}>
          {/* Colonna sinistra: lista clienti */}
          <div>{listaBody}</div>
          {/* Colonna destra: ranking fatturato */}
          <RankingTable
            title="Top Clienti per Fatturato"
            colNome="CLIENTE"
            colValore="FATTURATO"
            colCoda="% SUL TOTALE"
            rows={rankingClienti}
            getSub={(row) => `${Number(row.valore).toLocaleString('it-IT')} €`}
            renderCoda={renderBarraCoda}
          />
        </div>
      )}

      {showForm && (
        <Modal title={editing ? 'Modifica cliente' : 'Nuovo cliente'} onClose={() => setShowForm(false)}>
          <ClienteForm initial={editing || {}} onSave={salva} onCancel={() => setShowForm(false)} saving={saving} />
        </Modal>
      )}
      <NomeGiaPresenteModal info={nomeInfo} onClose={() => setNomeInfo(null)} onApriEsistente={apriClienteEsistente} />
      {showProspect && (
        <NuovoProspectModal
          onClose={() => setShowProspect(false)}
          onCreated={(nuovo) => { setLista((prev) => [nuovo, ...prev]); setShowProspect(false); }}
        />
      )}
      {dettaglio && (
        <ClienteDetail cliente={dettaglio} onClose={() => setDettaglio(null)}
          onEdit={(c) => { setEditing(c); setShowForm(true); setDettaglio(null); }}
          onDelete={elimina}
          onStatoChanged={(updated) => {
            setLista((prev) => prev.map((c) => c.id === updated.id ? updated : c));
            setDettaglio(updated);
          }} />
      )}
      {showImportPdf && (
        <ImportClientePdfModal
          onClose={() => setShowImportPdf(false)}
          onImported={(nuovoCliente) => { setLista((prev) => [nuovoCliente, ...prev]); }}
        />
      )}
      {transizione && (
        <LifecycleTransizioneModal
          cliente={transizione.cliente}
          statoTarget={transizione.statoTarget}
          onClose={() => setTransizione(null)}
          onSuccess={onTransizioneOk}
        />
      )}
    </div>
  );
}
